import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal, { ModalHeader, ModalBody, ModalFooter } from "@ddm-design-system/modal";
import { Body } from "@ddm-design-system/typography";
import { PrimaryButton, SecondaryButton } from "@ddm-design-system/button";
import { Checkbox } from "@ddm-design-system/checkbox";
import { useIsMobile } from "@ddm-design-system/hooks";
import StockListItem from "./StockListItem";
import { setStock } from "../../store/product/actions";
import { IAppState } from "../../store";
import { getProductsInfo } from "../../store/product/selectors";
import useContent from "../../hooks/useContent";
import { getOutletById } from "../../store/outlet/selectors";
import "./product.scss";

interface IProps {
  locationId: string;
  isOpen: boolean;
  onClose: () => void;
  showOutletName?: boolean;
}

interface IStock {
  [beverageId: string]: {
    kegsInStock: number | null;
    lastUpdatedStock: number;
  };
}

const StockManagementModal: React.FC<IProps> = ({
  locationId,
  isOpen,
  onClose,
  showOutletName
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const { managerAppProduct: content, managerAppCommon: contentCommon } = useContent();
  const [initStock, setInitStock] = useState<any>();
  const [currentLocationId, setCurrentLocationId] = useState<any>();
  const [beverageStock, setBeverageStock] = useState<any>({});
  const [updatedToday, setUpdatedToday] = useState(false);

  const outletInfo = useSelector(getOutletById(locationId));
  const products = useSelector((state: IAppState) => getProductsInfo(state, locationId)) || [];

  useEffect(() => {
    if ((!initStock && products.length) || currentLocationId !== locationId) {
      const stock: IStock = {};
      products.forEach(({ kegsInStock, lastUpdatedStock, beverage }) => {
        stock[beverage.id] = {
          kegsInStock,
          lastUpdatedStock
        };
      });
      setBeverageStock(stock);
      setInitStock(stock);
      setCurrentLocationId(locationId);
    }
  }, [initStock, products, currentLocationId, locationId]);

  const handleEdit = useCallback(() => {
    const newStock: any = [];
    Object.keys(beverageStock).forEach((b: string) => {
      if (initStock[b]?.kegsInStock !== beverageStock[b].kegsInStock) {
        newStock.push({
          beverageId: b,
          numberKegs: beverageStock[b].kegsInStock
        });
      }
    });
    dispatch(setStock(locationId, newStock, updatedToday));
    onClose();
  }, [dispatch, locationId, updatedToday, initStock, beverageStock, onClose]);

  const renderCheckbox = () => (
    <div className="stock-management-modal-checkbox">
      <Checkbox checked={updatedToday} onChange={e => setUpdatedToday(e.target.checked)}>
        <Body className="stock-management-modal-checkbox-text">
          {content.manager_app_product_purchasing_order_today}
        </Body>
      </Checkbox>
      <Body className="stock-management-modal-checkbox-desc">
        {content.manager_app_product_purchasing_order_today_desc}
      </Body>
    </div>
  );

  return (
    <Modal className="stock-management-modal" isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader
        headerTitle={content.manager_app_product_manage_stock}
        description={showOutletName ? outletInfo?.name : ""}
      />
      <ModalBody className="stock-management-modal-body">
        {isMobile && renderCheckbox()}
        <div className="stock-management-modal-body-container">
          {locationId === currentLocationId &&
            products.map(({ beverage, lastUpdatedStock }) => {
              const { kegsInStock } = beverageStock?.[beverage?.id];

              return (
                <StockListItem
                  key={beverage.id}
                  beverage={beverage}
                  stock={kegsInStock || 0}
                  lastUpdatedStock={lastUpdatedStock}
                  onChange={value =>
                    setBeverageStock({
                      ...beverageStock,
                      [beverage.id]: {
                        ...beverageStock[beverage.id],
                        kegsInStock: value
                      }
                    })
                  }
                />
              );
            })}
        </div>
        {!isMobile && renderCheckbox()}
      </ModalBody>
      <ModalFooter className="stock-management-modal-footer">
        <SecondaryButton onClick={onClose}>{contentCommon.common_cancel}</SecondaryButton>
        <PrimaryButton onClick={handleEdit}>{contentCommon.manager_app_save}</PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};

export default StockManagementModal;
